import type {
  ActionMessages,
  TypeChip,
  UrgencyBadges,
} from "./types/notification.types";

export const TYPE_CHIPS: TypeChip[] = [
  {
    key: "all",
    label: "All Alerts",
    icon: "🔔",
    iconStyle: { background: "var(--bg3)", border: "1px solid var(--bd)" },
    countClass: "",
  },
  {
    key: "flag",
    label: "Flags / Urgent",
    icon: "🚩",
    iconStyle: { background: "var(--rbg)", border: "1px solid var(--rbd)" },
    countClass: "urgent",
  },
  {
    key: "overdue",
    label: "Overdue",
    icon: "⏰",
    iconStyle: { background: "var(--rbg)", border: "1px solid var(--rbd)" },
    countClass: "warn",
  },
  {
    key: "draw",
    label: "Draw Activity",
    icon: "💰",
    iconStyle: { background: "var(--bl)", border: "1px solid var(--bm)" },
    countClass: "",
  },
  {
    key: "lien",
    label: "Lien Waivers",
    icon: "✍️",
    iconStyle: { background: "var(--gbg)", border: "1px solid var(--gbd)" },
    countClass: "ok",
  },
  {
    key: "report",
    label: "Reports",
    icon: "📋",
    iconStyle: { background: "var(--tbg)", border: "1px solid var(--tbd)" },
    countClass: "",
  },
  {
    key: "precon",
    label: "Pre-Con",
    icon: "📁",
    iconStyle: { background: "var(--pbg)", border: "1px solid var(--pbd)" },
    countClass: "",
  },
];

export const ACTION_MESSAGES: ActionMessages = {
  Dismiss: "Alert dismissed",
  "Send Reminder": "Reminder sent via email & SMS",
  "Send to Bank": "Report queued for bank delivery",
  "Resend Waiver": "Lien waiver resent via SMS & email",
  "Mark Resolved": "Marked as resolved",
  "Schedule Inspection": "Inspection scheduled - inspector notified",
  Approve: "Checklist approved",
  "View Draw": "Opening draw...",
  "View Report": "Opening report...",
  "Review Draw": "Opening draw review...",
  "View Project": "Opening project...",
  "View User": "Opening user profile...",
  "View Waiver": "Opening lien waiver...",
  "Call GC": "Calling GC...",
  "Call Sub": "Calling subcontractor...",
  "Assign Inspector": "Opening inspector assignment...",
  "View Inspection": "Opening inspection report...",
  "View Progress Report": "Opening progress report...",
  "Publish Report": "Opening report for publishing...",
  "Download PDF": "Preparing report for download...",
  "Notify Bank": "Bank has been notified",
  "Send Reminder to GC": "Reminder sent to GC",
  "Contact GC": "Contacting GC...",
  "Contact Bank": "Contacting bank...",
  "Start Close-Out": "Opening close-out...",
  "Open Mobile Inspector": "Opening inspector...",
  "Review Checklist": "Opening checklist...",
};

export interface NotificationActionConfig {
  label: string;
  primary?: boolean;
  type:
    | "navigate"
    | "phone"
    | "toast"
    | "email"
    | "contact_inspector"
    | "contact_gc";
}

export const NOTIFICATION_ACTION_CONFIG: Record<
  string,
  NotificationActionConfig
> = {
  // ── Navigation ──
  view_draw: { label: "View Draw", primary: true, type: "navigate" },
  review_draw: { label: "Review Draw", primary: true, type: "navigate" },
  view_project: { label: "View Project", primary: true, type: "navigate" },
  view_report: { label: "View Report", primary: true, type: "navigate" },
  view_inspection: {
    label: "View Inspection",
    primary: true,
    type: "navigate",
  },
  view_progress_report: {
    label: "View Progress Report",
    primary: true,
    type: "navigate",
  },
  publish_report: { label: "Publish Report", primary: true, type: "navigate" },
  view_waiver: { label: "View Waiver", primary: true, type: "navigate" },
  view_lien_waiver: { label: "View Waiver", primary: true, type: "navigate" },
  review_checklist: {
    label: "Review Checklist",
    primary: true,
    type: "navigate",
  },
  view_pre_con_checklist: {
    label: "Review Checklist",
    primary: true,
    type: "navigate",
  },
  assign_inspector: { label: "Assign Inspector", type: "navigate" },
  open_mobile_inspector: {
    label: "Open Mobile Inspector",
    primary: true,
    type: "navigate",
  },
  review_sov: { label: "Review SOV", primary: true, type: "navigate" },
  view_sov: { label: "View SOV", primary: true, type: "navigate" },
  submit_first_draw: {
    label: "Submit First Draw",
    primary: true,
    type: "navigate",
  },
  review_report: { label: "Review Report", primary: true, type: "navigate" },
  review_precon_report: {
    label: "Review Report",
    primary: true,
    type: "navigate",
  },
  review_pre_con_report: {
    label: "Review Report",
    primary: true,
    type: "navigate",
  },
  view_pre_con_report: {
    label: "Review Report",
    primary: true,
    type: "navigate",
  },
  view_precon: { label: "Review Report", primary: true, type: "navigate" },
  review_precon: { label: "Review Report", primary: true, type: "navigate" },
  view_precon_report: {
    label: "Review Report",
    primary: true,
    type: "navigate",
  },
  view_acknowledgement: {
    label: "View Acknowledgement",
    primary: true,
    type: "navigate",
  },
  view_acknowledgment: {
    label: "View Acknowledgement",
    primary: true,
    type: "navigate",
  },
  view_requirements_acknowledged: {
    label: "View Acknowledgement",
    primary: true,
    type: "navigate",
  },
  view_gc_requirements: {
    label: "View Acknowledgement",
    primary: true,
    type: "navigate",
  },
  view_user: { label: "View User", type: "navigate" },
  view_user_profile: { label: "View User", type: "navigate" },
  start_closeout: { label: "Start Close-Out", type: "navigate" },
  // ── Phone ──
  call_gc: { label: "Call GC", type: "phone" },
  call_sub: { label: "Call Sub", type: "phone" },
  // ── API / toast ──
  resend_waiver: { label: "Resend Waiver", primary: true, type: "toast" },
  send_reminder: { label: "Send Reminder", primary: true, type: "toast" },
  send_reminder_gc: { label: "Send Reminder to GC", type: "toast" },
  send_to_bank: { label: "Send to Bank", primary: true, type: "toast" },
  notify_bank: { label: "Notify Bank", type: "toast" },
  contact_gc: { label: "Contact GC", type: "contact_gc" },
  contact_bank: { label: "Contact Bank", type: "email" },
  contact_inspector: { label: "Contact Inspector", type: "contact_inspector" },
  download_report: { label: "Download PDF", type: "toast" },
  schedule_inspection: { label: "Schedule Inspection", type: "toast" },
  approve: { label: "Approve", primary: true, type: "toast" },
  mark_resolved: { label: "Mark Resolved", type: "toast" },
  dismiss: { label: "Dismiss", type: "toast" },
  approve_change_order: { label: "Approve", primary: true, type: "navigate" },
  reject_change_order: { label: "Reject", type: "navigate" },
  request_change_order_info: { label: "Request Info", type: "navigate" },
  view_change_order: { label: "View Details", primary: true, type: "navigate" },
};

export const URGENCY_BADGES: UrgencyBadges = {
  urgent: { className: "ub-urgent", label: "● Urgent" },
  action: { className: "ub-action", label: "● Action needed" },
  ok: { className: "ub-ok", label: "● Info" },
  info: { className: "ub-info", label: "● Info" },
};

export const EVENT_TYPE_ACTION_LABELS: Record<
  string,
  Record<string, string>
> = {
  OVERBILLING_FLAG_RAISED: { flagged: "Overbilling Flag" },
  RESIDENTIAL_OVERBILLING_FLAG_RAISED: { flagged: "Overbilling Flag" },
  draw_disbursement: {
    submitted: "Draw Submitted",
    approved: "Draw Approved",
    rejected: "Draw Rejected",
    flagged: "Overbilling Flag",
    signed: "Owner Signed Draw",
  },
  change_order: {
    submitted: "Change Order Submitted",
    approved: "Change Order Approved",
    rejected: "Change Order Rejected",
  },
  lien_waiver: {
    sent: "Waiver Sent",
    signed: "Waiver Signed",
    completed: "All Waivers Received",
  },
  overdue: {
    overdue: "Overdue",
  },
  inspection: {
    assigned: "Inspection Assigned",
    submitted: "Inspection Submitted",
  },
  report: {
    published: "Report Published",
  },
  pre_construction: {
    submitted: "Pre-Con Submitted",
    approved: "Pre-Con Approved",
    published: "Pre-Con Report Published",
    acknowledged: "Requirements Acknowledged",
  },
  milestone: {
    reached: "Milestone Reached",
    completed: "Substantial Completion",
  },
  user: {
    invited: "User Invited",
    activated: "User Activated",
    reinvited: "User Re-invited",
    forgot_password: "Password Reset",
    login_2fa: "Login Verification",
    login_alert: "New Login Alert",
    phone_verify: "Phone Verification",
  },
  project: {
    assigned: "Project Assigned",
  },
};
